import { Token } from '@stripe/stripe-js';

import {
  ErrorCodeEnum,
  Moving__LongDistanceMoveInputType,
  Moving__StoragePlanInput,
  Moving__WhiteGloveBundleEnum,
  useGeocodeQuery,
  useLongDistanceMoveCreateMutation,
  useOrderCancellationPolicyGroupQuery,
} from '@graphql/platform';

import { IMPACT_CLICK_ID } from '@root/initializers/impact';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { GQLError } from '@utils/gql_errors';
import { useReservationCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_reservation_completed_funnel_event';
import {
  EventName,
  ProductType,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import { useMovingCheckoutContext } from '../context';
import { MovingCheckoutData } from '../data';

export function useReserveLongDistanceMove() {
  const [longDistanceMoveCreate] = useLongDistanceMoveCreateMutation();
  const trackMoveReserved = useReservationCompletedFunnelEvent();
  const {
    flowState: { values },
  } = useMovingCheckoutContext();
  const geocode =
    useGeocodeQuery({
      variables: { zip: values.zip! },
      skip: !values.zip,
    }).data?.geocode ?? undefined;
  const { data } = useOrderCancellationPolicyGroupQuery({
    variables: { visitorToken: WT_VISITOR_TOKEN },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });
  const orderCancellationPolicyGroup =
    geocode?.region?.name === 'Los Angeles'
      ? undefined
      : data?.orderCancellationPolicyGroup;
  return async ({
    stripeToken,
    leadToken,
    values,
    storagePlanInput,
  }: {
    stripeToken?: Token;
    customerToken?: string;
    leadToken: string;
    values: MovingCheckoutData;
    storagePlanInput: Moving__StoragePlanInput;
  }) => {
    const {
      name,
      phone,
      email,
      dateScheduled,
      startAddress,
      movingQuote,
      unpackingScheduled,
      movingBundle,
    } = values;

    if (!movingQuote) {
      throw new Error(
        `Moving quote did not exist on checkout for lead (${leadToken}) & visitor (${WT_VISITOR_TOKEN})`,
      );
    }

    if (!dateScheduled) {
      throw new Error(
        'Pickup date/time selection is required to reserve your move.',
      );
    }

    if (
      !startAddress?.street ||
      !startAddress?.city ||
      !startAddress?.state ||
      !startAddress?.zip
    ) {
      throw new Error(
        'A complete pickup address is required to reserve your move.',
      );
    }

    const marketingData = IMPACT_CLICK_ID
      ? { impactClickID: IMPACT_CLICK_ID }
      : undefined;

    const deliveryDate = unpackingScheduled?.toISODate();

    const moveInput: Moving__LongDistanceMoveInputType = {
      movingQuoteSignedID: movingQuote.signedID,
      deliveryDate,
      premiumServicePackageSelected:
        movingBundle === Moving__WhiteGloveBundleEnum.FullService,
      onboardingInput: {
        ...storagePlanInput,
        visitorToken: WT_VISITOR_TOKEN,
        name: name,
        phone: phone,
        email: email,
        date: dateScheduled.fromTime,
        pickupAddress: {
          street: startAddress.street,
          city: startAddress.city,
          state: startAddress.state,
          zip: startAddress.zip,
        },
        stripeToken: stripeToken?.id,
        slaWindowDuration: dateScheduled.duration,
        orderCancellationPolicyGroupID: orderCancellationPolicyGroup?.id,
        marketingData,
      },
    };

    const { data } = await longDistanceMoveCreate({
      variables: { input: moveInput },
    });

    if (!data || data.longDistanceMoveCreate?.error) {
      throw new GQLError(
        data?.longDistanceMoveCreate?.error ?? {
          errorCode: ErrorCodeEnum.Unknown,
          errorMessage: 'Unknown server error',
        },
      );
    }

    createThirdPartyConversionEvent(EventName.CHECKOUT, {
      value: movingQuote.laborRate?.amount,
      zip_code: startAddress?.zip,
      product_type: ProductType.MOVING,
      currency: 'USD',
      orderID: data.longDistanceMoveCreate?.orderID,
    });

    const accountPortalUrl = data.longDistanceMoveCreate?.accountPortalUrl;
    const orderToken = data.longDistanceMoveCreate?.orderToken;

    if (!accountPortalUrl || !orderToken) {
      throw new Error('Create move failed');
    }
    trackMoveReserved({ orderId: data.longDistanceMoveCreate.orderID! });

    return {
      accountPortalUrl,
      orderToken,
    };
  };
}
